import { useState } from "react";
import { Card, Typography, TextField, Button, IconButton, InputAdornment } from "@mui/material";
import { FaLock, FaSyncAlt } from "react-icons/fa";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import COLORS from "../../../../../constants/colors";
import SnackbarAlert from "../../Reusbale/SnackbarAlert";
import baseURL from "../../config";
import { toast } from "react-toastify";

const PasswordReset = () => {
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [alert, setAlert] = useState({ open: false, message: "", severity: "error" });

  const handlePasswordChange = (e) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
  };

  const toggleVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleCloseSnackbar = () => {
    setAlert({ ...alert, open: false });
  };

  const handleSubmit = async () => {
    const { currentPassword, newPassword, confirmNewPassword } = passwords;

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      toast.error("All fields are required.");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("New password must be at least 8 characters long.");

      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error("New password and confirmation do not match.");

      return;
    }

    try {
      const response = await fetch(`${baseURL}/user/update-password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update password.");
      }

      toast.success("Password updated successfully!");


      // Clear input fields after successful update
      setPasswords({ currentPassword: "", newPassword: "", confirmNewPassword: "" });
    } catch (error) {
      toast.success(error.message,);

    }
  };

  return (
    <Card sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", p: 3, textAlign: "left", mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "1.05rem", letterSpacing: "-0.3px", mb: 3, display: "flex", alignItems: "center" }}>
        <FaLock style={{ marginRight: 8, color: "#2D4A3D" }} />
        Update Password
      </Typography>

      <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
        Use a strong password with at least 8 characters, including uppercase letters, numbers, and special characters. Keep your password private.
      </Typography>

      <TextField
        label="Current Password"
        type={showPassword.current ? "text" : "password"}
        variant="outlined"
        fullWidth
        name="currentPassword"
        value={passwords.currentPassword}
        onChange={handlePasswordChange}
        sx={inputStyles}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => toggleVisibility("current")} edge="end">
                {showPassword.current ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        label="New Password"
        type={showPassword.new ? "text" : "password"}
        variant="outlined"
        fullWidth
        name="newPassword"
        value={passwords.newPassword}
        onChange={handlePasswordChange}
        sx={inputStyles}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => toggleVisibility("new")} edge="end">
                {showPassword.new ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        label="Confirm New Password"
        type={showPassword.confirm ? "text" : "password"}
        variant="outlined"
        fullWidth
        name="confirmNewPassword"
        value={passwords.confirmNewPassword}
        onChange={handlePasswordChange}
        sx={inputStyles}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => toggleVisibility("confirm")} edge="end">
                {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button variant="contained" size="small" onClick={handleSubmit} sx={buttonStyles}>
        <FaSyncAlt style={{ fontSize: "16px" }} />
        Update Password
      </Button>

      <SnackbarAlert open={alert.open} message={alert.message} severity={alert.severity} handleClose={handleCloseSnackbar} />
    </Card>
  );
};

const inputStyles = {
  mb: 2,
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#f5f5f5",
  },
};

const buttonStyles = {
  backgroundColor: COLORS.primary,
  textTransform: "none",
  py: 1.2,
  px: 2,
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  gap: 1,
  "&:hover": { backgroundColor: COLORS.primary, opacity: 0.9 },
};

export default PasswordReset;
